import {Component, computed, inject, OnInit, signal, TemplateRef, ViewChild, WritableSignal, DestroyRef} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ServiceRequestsService} from "../../../services/service-requests.service";
import {ActivatedRoute} from "@angular/router";
import {constructColumns, ServiceRequestsTableColumn} from "./service-requests-table.config";
import {DateUtils} from "../../../utils/date-utils";
import {TableData} from "../../../interfaces/table/table-data";
import {auditTime, debounceTime, distinctUntilChanged, merge, Subject, switchMap, tap} from "rxjs";
import {TableParams} from "../../../interfaces/table/table-params";
import {FormBuilder, FormGroup} from "@angular/forms";
import {ObjectUtils} from "../../../utils/object-utils";
import {TableAction} from "../../../interfaces/table/table-action";
import {TableSort} from "../../../interfaces/table/table-sort";
import {StatusCategory} from "../../../enums/status-category";
import {ServiceRequestStatus} from "../../../enums/service-request-status.enum";

@Component({
  selector: 'app-service-requests',
  templateUrl: './service-requests.component.html',
  styleUrls: ['./service-requests.component.css'],
  standalone: false
})
export class ServiceRequestsComponent implements OnInit {
  readonly allStatusesOption = {service_request_status_id: 0, service_request_status_name: 'All Statuses'}
  statuses: any[] = [this.allStatusesOption];
  readonly allLocationsOption = {location_id: 0, location_name: 'All Locations'};
  locations: any[] = [this.allLocationsOption];
  readonly defaultSortParams: TableSort = {
    columnId: ServiceRequestsTableColumn.ServiceRequestNumber,
    direction: 'desc'
  };

  tab: 'pending' | 'completed' = 'pending';
  filterForm!: FormGroup;
  isPdfExportRunning: boolean = false;

  pendingTableData = signal<TableData>({data: [], total: 0});
  loadingPendingTableData = signal<boolean>(true);
  loadPending$ = new Subject<{ tableParams: TableParams, filterParams: any }>();
  pendingTableParams: TableParams = {currentPage: 1, pageSize: 10, sortParams: null};
  currentPendingTablePage = signal<number>(1);

  completedTableData = signal<TableData>({data: [], total: 0});
  loadingCompletedTableData = signal<boolean>(true);
  loadCompleted$ = new Subject<{ tableParams: TableParams, filterParams: any }>();
  completedTableParams: TableParams = {currentPage: 1, pageSize: 10, sortParams: null};
  currentCompletedTablePage = signal<number>(1);

  tableColumns = computed(() =>
    constructColumns(this.serviceRequestNumberAssetColumn, this.assetsColumn));

  @ViewChild('serviceRequestNumberAssetColumn', {static: true}) serviceRequestNumberAssetColumn!: TemplateRef<any>;
  @ViewChild('assetsColumn', {static: true}) assetsColumn!: TemplateRef<any>;
  readonly unexpandedAssetsCount = 3;
  expandedAssets = new Set<string>();

  tableActions: TableAction[] = [
    {
      displayName: 'Export as PDF',
      execute: (row) => this.openPdf(row[ServiceRequestsTableColumn.ServiceRequestNumber]),
      backgroundColor: '#fcd34d'
    }
  ];

  protected readonly ServiceRequestsTableColumn = ServiceRequestsTableColumn;
  private readonly serviceRequestService = inject(ServiceRequestsService);
  private readonly activeRouter = inject(ActivatedRoute);
  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);

  async ngOnInit(): Promise<void> {
    this.initFilterForm();
    this.initServiceRequestsObservable(
      this.loadPending$,
      this.pendingTableData,
      this.loadingPendingTableData,
      StatusCategory.Pending,
      (data) => data
    );
    this.initServiceRequestsObservable(
      this.loadCompleted$,
      this.completedTableData,
      this.loadingCompletedTableData,
      StatusCategory.Completed,
      (data) => data.filter((item: any) =>
        item.ServiceRequestStatusId !== ServiceRequestStatus.Completed && item.ServiceRequestStatusId !== ServiceRequestStatus.Canceled)
    );

    this.refreshData();

    this.serviceRequestService.getLocations().then((data: any) => {
      this.locations = [this.allLocationsOption, ...data];
    });

    await this.serviceRequestService.getServiceRequestsStatuses().then((data: any) => {
      this.statuses = [this.allStatusesOption, ...data];
      this.getFilterStatusId();
    });
  }

  getFilterStatusId() {
    const statusName = this.activeRouter.snapshot.queryParams['status'];
    if (!statusName) return;

    const status = this.statuses.find((item: any) =>
      item.service_request_status_name.replaceAll(' ', '_').toLowerCase() == statusName);
    if (status) {
      this.filterForm.get('status')!.setValue(status.service_request_status_id);
    }
  }

  initFilterForm() {
    this.filterForm = this.fb.group({
      serviceRequestsLookup: [''],
      location: [0],
      status: [0]
    });

    this.filterForm.get('serviceRequestsLookup')!.valueChanges.pipe(
      debounceTime(1000),
      distinctUntilChanged(),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.currentPendingTablePage.set(1);
      this.currentCompletedTablePage.set(1);
      this.pendingTableParams = {...this.pendingTableParams, currentPage: 1};
      this.completedTableParams = {...this.completedTableParams, currentPage: 1};
      this.refreshData();
    });

    merge(
      this.filterForm.get('location')!.valueChanges,
      this.filterForm.get('status')!.valueChanges
    ).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.syncTabToStatusFilter();
      this.currentPendingTablePage.set(1);
      this.currentCompletedTablePage.set(1);
      this.pendingTableParams = {...this.pendingTableParams, currentPage: 1};
      this.completedTableParams = {...this.completedTableParams, currentPage: 1};
      this.refreshData();
    });
  }

  initServiceRequestsObservable(
    subject: Subject<{ tableParams: TableParams, filterParams: any }>,
    tableDataSignal: WritableSignal<TableData>,
    loadingSignal: WritableSignal<boolean>,
    statusCategoryId: string,
    filterFn: (data: any[]) => any[]) {
    subject.pipe(
      distinctUntilChanged((prev, curr) => ObjectUtils.deepEqual(prev, curr)),
      tap(_ => loadingSignal.set(true)),
      auditTime(0),
      switchMap(params => {
        const {tableParams, filterParams} = params;
        const offset = (tableParams.currentPage - 1) * tableParams.pageSize;
        const sortParams = tableParams.sortParams ?? this.defaultSortParams;
        return this.serviceRequestService.getServiceRequests(offset, tableParams.pageSize, filterParams.serviceRequestsLookup,
          sortParams, filterParams.location, filterParams.status, statusCategoryId);
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((response: any) => {
      loadingSignal.set(false);

      const data = filterFn(response.map((item: any) => ({
        ServiceRequestNumber: item.service_request_number,
        ServiceRequestStatusName: {name: item.service_request_status_name, color: item.service_request_status_color},
        asset: item.assets,
        LocationName: item.location_name,
        CreatedFullName: item.created_full_name,
        CreatedDate: DateUtils.formatDate(item.CreatedDate)
      })));
      tableDataSignal.set({
        data: data,
        total: response.length !== 0 ? response[0].total_records : 0,
      });
    });
  }

  refreshData() {
    // Read each control's own value rather than the parent FormGroup's
    // aggregated `.value`: a child control's valueChanges fires before
    // Angular propagates the update up to recompute the parent's value,
    // so callers subscribed to child valueChanges (see initFilterForm)
    // would otherwise read a filter value that's one selection stale.
    const filterParams = {
      serviceRequestsLookup: this.filterForm.get('serviceRequestsLookup')!.value,
      location: this.filterForm.get('location')!.value,
      status: this.filterForm.get('status')!.value
    };
    if (this.tab === 'pending') {
      const tableParams = {...this.pendingTableParams};
      this.loadPending$.next({tableParams, filterParams});
    } else if (this.tab === 'completed') {
      const tableParams = {...this.completedTableParams};
      this.loadCompleted$.next({tableParams, filterParams});
    }
  }

  // Status filter is shared across both tabs, but each tab only queries its own
  // status category (see initServiceRequestsObservable). Selecting a status that
  // lives in the other category would otherwise return an empty table. Move the
  // user to the tab that actually holds the selected status. 'All Statuses' (0)
  // keeps the current tab.
  syncTabToStatusFilter() {
    const statusId = String(this.filterForm.get('status')!.value);
    if (statusId === ServiceRequestStatus.Completed || statusId === ServiceRequestStatus.Canceled) {
      this.tab = 'completed';
    } else if (statusId !== String(this.allStatusesOption.service_request_status_id)) {
      this.tab = 'pending';
    }
  }

  changeTab(newTab: 'pending' | 'completed') {
    this.tab = newTab;
    this.refreshData();
  }

  onPendingTableParamChange(event: TableParams) {
    this.pendingTableParams = event;
    this.refreshData();
  }

  onCompletedTableParamChange(event: TableParams) {
    this.completedTableParams = event;
    this.refreshData();
  }

  isSelectClearable(controlName: string) {
    return this.filterForm.get(controlName)?.value !== 0;
  }

  clearSelectValue(controlName: string) {
    this.filterForm.get(controlName)?.setValue(0);
  }

  getVisibleAssets(items: any[]): any[] {
    return items.slice(0, this.unexpandedAssetsCount);
  }

  getHiddenAssets(items: any[]): any[] {
    return items.slice(this.unexpandedAssetsCount);
  }

  toggleExpandedAssets(rowId: string, value: boolean) {
    if (value) {
      this.expandedAssets.delete(rowId);
    } else {
      this.expandedAssets.add(rowId);
    }
  }

  isAssetExpanded(rowId: string): boolean {
    return this.expandedAssets.has(rowId);
  }

  openPdf(serviceRequestId: string) {
    this.isPdfExportRunning = true
    this.serviceRequestService.GetServiceRequestPdf(serviceRequestId).then((res: any) => {
      if (res) {
        window.open(res.Data.Url, '_blank');
      }
    }).finally(() => this.isPdfExportRunning = false);
  }
}
